import * as React from 'react'
import { Link2, Loader2, Upload } from 'lucide-react'

import { FolderManager } from '@/components/assets/FolderManager'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/use-toast'
import { useUploadAsset } from '@/hooks/useAssets'
import { useUploadReference } from '@/hooks/useReferences'
import { cn } from '@/lib/utils'
import type { AssetFolder } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  folders: AssetFolder[]
  onAssetAdded: () => void
}

const CREATE_NEW_VALUE = '__create_new__'
const NO_FOLDER_VALUE = '__none__'

interface QueuedFile {
  file: File
  id: string
  isVideo: boolean
}

export function AddAssetDialog({ open, onOpenChange, folders, onAssetAdded }: Props) {
  const { toast } = useToast()
  const { upload: uploadAsset, uploading: uploadingAsset } = useUploadAsset()
  const { upload: uploadReference, addUrl, uploading: uploadingReference } = useUploadReference()

  const [selectedFolderId, setSelectedFolderId] = React.useState<string>('')
  const [createFolderOpen, setCreateFolderOpen] = React.useState(false)
  const [queuedFiles, setQueuedFiles] = React.useState<QueuedFile[]>([])
  const [dragOver, setDragOver] = React.useState(false)
  const [url, setUrl] = React.useState('')
  const [analyzeAsReference, setAnalyzeAsReference] = React.useState(false)

  const inputRef = React.useRef<HTMLInputElement>(null)

  const uploading = uploadingAsset || uploadingReference

  // Derived state
  const hasFolder = selectedFolderId !== '' && selectedFolderId !== NO_FOLDER_VALUE
  const hasVideos = queuedFiles.some((f) => f.isVideo)

  // Reset all state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedFolderId(folders.length > 0 ? String(folders[0].id) : '')
      setQueuedFiles([])
      setUrl('')
      setAnalyzeAsReference(false)
    }
  }, [open, folders])

  // Auto-toggle analyzeAsReference based on folder selection:
  // Default ON when no folder selected, OFF when folder selected.
  // Only applies when videos are present and the user hasn't manually overridden.
  const prevHasFolderRef = React.useRef(hasFolder)
  React.useEffect(() => {
    if (prevHasFolderRef.current !== hasFolder) {
      setAnalyzeAsReference(!hasFolder)
      prevHasFolderRef.current = hasFolder
    }
  }, [hasFolder])

  const handleFolderSelectChange = (value: string) => {
    if (value === CREATE_NEW_VALUE) {
      setCreateFolderOpen(true)
    } else {
      setSelectedFolderId(value)
    }
  }

  const handleFolderCreated = () => {
    onAssetAdded()
  }

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    const incoming: QueuedFile[] = Array.from(fileList).map((file) => ({
      file,
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      isVideo: file.type.startsWith('video/'),
    }))
    setQueuedFiles((prev) => {
      // Deduplicate by name+size
      const existingKeys = new Set(prev.map((f) => `${f.file.name}-${f.file.size}`))
      const filtered = incoming.filter(
        (f) => !existingKeys.has(`${f.file.name}-${f.file.size}`)
      )
      return [...prev, ...filtered]
    })
  }

  const removeFile = (id: string) => {
    setQueuedFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!uploading) addFiles(e.dataTransfer.files)
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!uploading) setDragOver(true)
  }

  const resolvedFolderId = hasFolder ? Number(selectedFolderId) : null

  const canSubmit =
    !uploading &&
    (queuedFiles.length > 0 || url.trim().length > 0) &&
    // Images always need a folder
    (!queuedFiles.some((f) => !f.isVideo) || resolvedFolderId !== null)

  const handleSubmit = async () => {
    if (!canSubmit) return

    let anySuccess = false

    // Process each queued file
    for (const queued of queuedFiles) {
      try {
        if (!queued.isVideo) {
          // Images always go to asset upload
          await uploadAsset(resolvedFolderId!, queued.file)
          toast({ title: 'Uploaded', description: queued.file.name })
        } else if (analyzeAsReference) {
          // Video with "Analyze as reference" ON
          await uploadReference(queued.file)
          toast({
            title: 'Reference uploaded',
            description: `${queued.file.name} — analysis started.`,
          })
        } else {
          // Video with "Analyze as reference" OFF — needs a folder
          if (resolvedFolderId === null) {
            toast({
              title: 'No folder selected',
              description: `Select a folder to upload ${queued.file.name} as an asset.`,
              variant: 'destructive',
            })
            continue
          }
          await uploadAsset(resolvedFolderId, queued.file)
          toast({ title: 'Uploaded', description: queued.file.name })
        }
        anySuccess = true
      } catch (err) {
        toast({
          title: 'Upload failed',
          description: (err as Error).message,
          variant: 'destructive',
        })
      }
    }

    // Process URL if provided
    if (url.trim()) {
      try {
        await addUrl(url.trim())
        toast({ title: 'Reference added', description: 'Analysis started from URL.' })
        anySuccess = true
      } catch (err) {
        toast({
          title: 'Failed to add URL',
          description: (err as Error).message,
          variant: 'destructive',
        })
      }
    }

    if (anySuccess) {
      onAssetAdded()
      onOpenChange(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Assets</DialogTitle>
          </DialogHeader>

          <div className="mt-2 space-y-5">
            {/* Folder selector */}
            <div className="space-y-1.5">
              <Label htmlFor="folder-select">
                Destination folder
                {queuedFiles.some((f) => !f.isVideo) && (
                  <span className="ml-1 text-xs text-destructive">*</span>
                )}
              </Label>
              <Select
                value={selectedFolderId || NO_FOLDER_VALUE}
                onValueChange={handleFolderSelectChange}
              >
                <SelectTrigger id="folder-select" className="w-full">
                  <SelectValue placeholder="Select a folder..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_FOLDER_VALUE}>
                    <span className="text-muted-foreground">No folder (reference videos only)</span>
                  </SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={String(folder.id)}>
                      {folder.name}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({folder.asset_count})
                      </span>
                    </SelectItem>
                  ))}
                  <SelectItem value={CREATE_NEW_VALUE}>+ Create new folder</SelectItem>
                </SelectContent>
              </Select>
              {queuedFiles.some((f) => !f.isVideo) && !hasFolder && (
                <p className="text-xs text-destructive">
                  A folder is required for image files.
                </p>
              )}
            </div>

            {/* Drop zone */}
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={() => setDragOver(false)}
              onClick={() => !uploading && inputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Upload files — click or drag and drop"
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !uploading) {
                  inputRef.current?.click()
                }
              }}
              className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                dragOver
                  ? 'border-foreground bg-accent/50'
                  : 'border-border hover:border-muted-foreground',
                uploading && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files)
                  e.target.value = ''
                }}
                disabled={uploading}
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drop files here or{' '}
                    <span className="underline underline-offset-2">browse</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Images and videos up to 500 MB
                  </p>
                </div>
              )}
            </div>

            {/* Queued file list */}
            {queuedFiles.length > 0 && (
              <ul className="space-y-1" aria-label="Files queued for upload">
                {queuedFiles.map((queued) => (
                  <li
                    key={queued.id}
                    className="flex items-center justify-between rounded-md bg-muted px-3 py-1.5 text-sm"
                  >
                    <span className="truncate max-w-[360px]" title={queued.file.name}>
                      {queued.isVideo ? '🎬' : '🖼'} {queued.file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(queued.id)}
                      aria-label={`Remove ${queued.file.name}`}
                      className="ml-2 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                      disabled={uploading}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Analyze as reference toggle — only shown when videos are queued */}
            {hasVideos && (
              <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                <div className="space-y-0.5">
                  <Label htmlFor="analyze-toggle" className="text-sm font-medium">
                    Analyze as reference
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Run the reference analysis pipeline on video files
                  </p>
                </div>
                <Switch
                  id="analyze-toggle"
                  checked={analyzeAsReference}
                  onCheckedChange={setAnalyzeAsReference}
                  disabled={uploading}
                  aria-describedby="analyze-toggle-desc"
                />
              </div>
            )}

            <Separator />

            {/* URL paste */}
            <div className="space-y-1.5">
              <Label htmlFor="url-input">Or add a video URL</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    id="url-input"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://..."
                    className="pl-9 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSubmit()
                    }}
                    disabled={uploading}
                    aria-label="Video URL for reference analysis"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                URL-added videos always go through the reference analysis pipeline.
              </p>
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between pt-1">
              {folders.length === 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setCreateFolderOpen(true)}
                  disabled={uploading}
                >
                  Create first folder
                </Button>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  aria-busy={uploading}
                >
                  {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Folder creation dialog — rendered outside to avoid nested dialog issues */}
      <FolderManager
        mode="create"
        folderType="sku"
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        onSuccess={handleFolderCreated}
      />
    </>
  )
}
